import { nextStep, PATH_INVITES, reachedCount } from '@alphadesk/ontology/objects/path.ts';
import { counted } from '@alphadesk/ontology/primitives.ts';
import type { PathRead } from '@alphadesk/web/contract.ts';
import clsx from 'clsx';
import { Check } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useLinkClick } from '../lib/router.ts';
import { Region } from './home/Region.tsx';
import { usePath } from './Invite.tsx';
import { buttonClass } from './primitives/button.tsx';
import { linkClass } from './primitives/controls.tsx';
import { CARD, Hint } from './primitives/shell.tsx';

type Row = PathRead['steps'][number];

const TITLE = 'Earn your invites';

const STRAIGHT_IN = `Do all ${PATH_INVITES} and your invites let a friend straight in, with no wait for the admins.`;

function Mark({ row, index }: { row: Row; index: number }) {
  return row.reached ? (
    <span className="grid size-5 shrink-0 place-items-center rounded-full bg-accent/10 text-accent">
      <Check aria-hidden className="size-3" strokeWidth={3} />
    </span>
  ) : (
    <span className="tnum grid size-5 shrink-0 place-items-center rounded-full border border-line text-3xs font-semibold text-ink-faint">
      {index + 1}
    </span>
  );
}

function Next({ row, index }: { row: Row; index: number }) {
  const onClick = useLinkClick();
  return (
    <li className="flex items-start gap-2.5 rounded-control border border-accent/40 bg-accent/5 p-3">
      <Mark row={row} index={index} />
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
          <span className={CARD}>{row.title}</span>
          <span className="text-2xs font-medium text-accent">+1 invite</span>
        </div>
        <Hint className="text-pretty">{row.why}</Hint>
        <a
          href={row.href}
          onClick={onClick}
          className={clsx(buttonClass('primary', 'sm'), 'self-start')}
        >
          {row.action}
        </a>
      </div>
    </li>
  );
}

function Other({ row, index }: { row: Row; index: number }) {
  const onClick = useLinkClick();
  return (
    <li className="flex items-start gap-2.5 px-3 py-1.5">
      <Mark row={row} index={index} />
      <div className="flex min-w-0 flex-1 flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 text-xs leading-5">
        {row.reached ? (
          <>
            <span className="text-ink-muted">{row.title}</span>
            <span className="text-2xs text-ink-faint">invite earned</span>
          </>
        ) : (
          <a href={row.href} onClick={onClick} className={linkClass('row', 'text-ink')}>
            {row.title}
          </a>
        )}
      </div>
    </li>
  );
}

function Steps({ path }: { path: PathRead }) {
  const next = nextStep(path);
  return (
    <ol aria-label="The steps" className="flex flex-col gap-0.5">
      {path.steps.map((row, index) =>
        row.step === next ? (
          <Next key={row.step} row={row} index={index} />
        ) : (
          <Other key={row.step} row={row} index={index} />
        ),
      )}
    </ol>
  );
}

const Count = ({ path }: { path: PathRead }) => (
  <span className="tnum text-xs text-ink-faint">
    {reachedCount(path)} of {PATH_INVITES}
  </span>
);

const HIDE_KEY = 'alphadesk.path.hidden';

function hiddenAt(): number | null {
  try {
    const raw = window.localStorage.getItem(HIDE_KEY);
    return raw === null ? null : Number.parseInt(raw, 10);
  } catch {
    return null;
  }
}

export interface PathCardState {
  path: PathRead;
  hide: () => void;
}

/** Null once the path is done, or while hidden at the count of steps it was hidden at. */
export function usePathCard(): PathCardState | null {
  const { path, refresh } = usePath();
  const [hidden, setHidden] = useState(hiddenAt);
  useEffect(() => refresh(), [refresh]);
  if (path === null || path.admits) return null;
  const reached = reachedCount(path);
  if (hidden === reached) return null;
  return {
    path,
    hide: () => {
      try {
        window.localStorage.setItem(HIDE_KEY, String(reached));
      } catch {}
      setHidden(reached);
    },
  };
}

/** The card's first step asks what Needs you's follow-an-analyst line would. */
export const coversFollowing = (card: PathCardState | null): boolean =>
  card !== null && nextStep(card.path) === 'follow';

export function PathCard({ card }: { card: PathCardState | null }) {
  if (card === null) return null;
  return (
    <Region title={TITLE} aside={<Count path={card.path} />}>
      <Steps path={card.path} />
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <Hint faint>{STRAIGHT_IN}</Hint>
        <button type="button" onClick={card.hide} className={linkClass('faint')}>
          Hide until my next step
        </button>
      </div>
    </Region>
  );
}

/** Under the invite on the account page; once done, only what friends earned. */
export function PathList({ path }: { path: PathRead }) {
  const friends = path.friendsDone > 0 && (
    <Hint faint>
      {counted(path.friendsDone, 'friend', 'friends')} you brought in finished their own path:{' '}
      {counted(path.friendsDone, 'invite', 'invites')} more.
    </Hint>
  );
  if (path.admits) return friends || null;
  return (
    <section className="flex flex-col gap-2.5 border-t border-line-soft pt-3">
      <div className="flex items-baseline justify-between gap-3">
        <span className={CARD}>{TITLE}</span>
        <Count path={path} />
      </div>
      <Steps path={path} />
      {friends}
      <Hint faint>
        {STRAIGHT_IN} A friend you bring in who finishes their own path earns you one more.
      </Hint>
    </section>
  );
}
